import { InputType } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { EmailAssigneeOptions, TaskAssigneeOptions } from 'pages/Tasks/constants'
import { getLoanTypeMapForSelect, partiesMap } from 'reducers/loanDetail.reducer'
import { timesOption } from 'stories/layouts/FloaterTabs/NotifyBorrower'

export const FileExtensionOptions = {
  pdf: 'PDF',
  xls: 'Xls',
  xlsx: 'Xlsx',
  xlsm: 'Xlsm',
  doc: 'Doc',
  docx: 'Docx',
  msg: 'Msg',
  jpeg: 'JPEG',
  jpg: 'JPG',
  png: 'PNG',
  svg: 'Svg',
  wav: 'Wav',
  xml: 'XML',
  html: 'HTML',
}

export interface TabConfig {
  key: string
  title: string
  fields: (loanGlobalStatus: any) => Record<string, InputType>
}

/**
 * Whether a payoff bills the payoff date itself. Values match the backend InterestAccrualEnd enum.
 * Through is always exactly one more day of interest than To.
 */
const interestAccrualEndOptions = {
  to: 'To Payoff Date',
  through: 'Through Payoff Date',
}

/**
 * Rate & Payment Changes: how a rate change dated inside a billing period prices it, and whether it
 * recalculates an amortizing loan's P&I. Values match the backend `RateChangeAccrual` /
 * `RateChangeRecast`; a loan overrides either on Servicing Information.
 */
const rateChangeAccrualOptions = {
  daily: 'Split by day',
  periodStart: 'Whole period at the rate in effect when it starts',
  dueDate: 'Whole period at the rate in effect on the due date',
}

const rateChangeRecastOptions = {
  recast: 'Recalculate P&I over the remaining term',
  keep: 'Keep the current P&I',
}

/**
 * Where funds paid over what the loan owes are credited. Values match the backend rules config
 * (`paymentOverpaymentAppliesTo`) and the payment import's own `overpayment` option.
 */
const overpaymentAppliesToOptions = {
  suspense: 'Suspense (held for the borrower)',
  principal: 'Principal (treated as a curtailment)',
}

/**
 * What the borrower portal shows for a loan, across both halves of it: whose SERVICING figures
 * (ours, FCI's, or none) and whether the ORIGINATION side is shown at all. Values match the
 * backend BorrowerPortalShows enum, and the first two are the ones this setting has always
 * carried, so existing companies keep meaning what they meant. A loan overrides this from
 * Servicing Information; a loan not boarded at FCI always shows our own servicing data.
 */
const borrowerPortalServicingSourceOptions = {
  nexys: 'Our servicing data and our origination data',
  fci: 'FCI servicing data and our origination data',
  none: 'No servicing data and our origination data',
  nexysNoOrigination: 'Our servicing data and no origination data',
  fciNoOrigination: 'FCI servicing data and no origination data',
}

export const paymentProviderOptions = (vendor: Record<string, any> = {}) => {
  const options: Record<string, string> = {}
  if (vendor.Stripe) options.stripe = 'Stripe'
  if (vendor.Usio) options.usio = 'USIO'
  return options
}

export const getTabConfigs = (
  loanGlobalStatus: any,
  loanServicingStatus: any,
  vendor: Record<string, any> = {},
): TabConfig[] => [
  {
    key: 'emailRules',
    title: 'Email Rules',
    fields: () => ({
      emailWithdrawnLoans: {
        inputType: 'multiselect',
        title: 'Email Receivers of Withdrawn/Declined Loans',
        options: accountTypes,
        defaultSelected: false,
        required: true,
      },
      emailSurveyNotification: {
        inputType: 'multiselect',
        title: 'Email Receivers of Survey Notification',
        options: accountTypes,
        defaultSelected: false,
        required: true,
      },
      emailWeeklyReport: {
        inputType: 'multiselect',
        title: 'Email Receivers of Weekly Loan Registration Report',
        options: EmailAssigneeOptions(),
        defaultSelected: false,
        required: true,
      },
    }),
  },
  {
    key: 'taskGeneration',
    title: 'Task Generation',
    fields: () => ({
      revalidateLoan: {
        inputType: 'multiselect',
        title: 'HUD1 Change - Loan Revalidation',
        options: TaskAssigneeOptions(),
        defaultSelected: false,
        required: true,
      },
      loanStructureRevalidation: {
        inputType: 'multiselect',
        title: 'Loan Structure Change - Loan Revalidation',
        options: TaskAssigneeOptions(),
        defaultSelected: false,
        required: true,
      },
      orderValuationMessage: {
        inputType: 'multiselect',
        title: 'Task Creation from profiles for Order Valuation Message',
        options: accountTypes,
        defaultSelected: false,
        required: true,
      },
    }),
  },
  {
    key: 'loanApplication',
    title: 'Loan Application',
    fields: () => ({
      trackRecordRequire: {
        inputType: 'togglebutton',
        title: '"Track Record is required when experience is >0" Prior to Loan Application Being Signed',
        span: 'full',
      },
      assetVerifyRequire: {
        inputType: 'togglebutton',
        title: '"Verify Assets is required"  Prior to Loan Application Being Signed',
        span: 'full',
      },
      loanStructurePriceChooseRequire: {
        inputType: 'togglebutton',
        title: 'Require to choose Loan Structure/Price before Borrower to sign the application.',
        span: 'full',
      },
      enableWetSign: {
        inputType: 'togglebutton',
        title: 'Enable Wet Signed Loan App Logic',
      },
      enableOverrideSign: {
        inputType: 'togglebutton',
        title: 'Enable Override Loan App Logic',
      },
      applyTridRules: {
        inputType: 'togglebutton',
        title: 'Apply TRID Rules',
        span: 'full',
      },
      maximumBorrower: {
        inputType: 'text',
        type: 'number',
        title: 'Maximum Borrower',
        maxNumberRange: 8,
      },
    }),
  },
  {
    key: 'loanSubmissionNotification',
    title: 'Loan Submission / Notification',
    fields: () => ({
      unClearWhenNewDocument: {
        inputType: 'toggle',
        title: 'Unclear condition for new document upload',
        value: false,
        span: 'full',
      },
      borrowerAutoNotifyLoanStatus: {
        inputType: 'multiselect',
        title: 'Loan Status',
        options: loanGlobalStatus,
        defaultSelected: false,
      },
      borrowerAutoNotifyLoanTypes: {
        inputType: 'multiselect',
        title: 'Loan Channel',
        options: getLoanTypeMapForSelect(),
        defaultSelected: false,
      },
      borrowerAutoNotifyFrequency: {
        inputType: 'text',
        type: 'number',
        title: 'Days Reminder Frequency',
      },
      borrowerAutoNotifyTime: {
        inputType: 'select',
        title: 'Time',
        options: timesOption,
        allowDefaultOption: false,
        hasDefaultOption: true,
      },
      defaultNotifyParties: {
        inputType: 'multiselect',
        title: 'Default Notification Profiles',
        options: {
          creator: partiesMap.creator,
          loanProcessor: partiesMap.loanProcessor,
          loanOfficer: partiesMap.loanOfficer,
          broker: partiesMap.broker,
          accountExecutive: partiesMap.accountExecutive,
          underwriter: partiesMap.underwriter,
          accountManager: partiesMap.accountManager,
        },
        defaultSelected: false,
        span: 'full',
        tooltip:
          'Selected loan parties are automatically added as To recipients for Third Party Originator notifications.',
      },
      autoNotifyOnHoldTransition: {
        inputType: 'toggle',
        title: 'Email the borrower when a loan moves from Loan Setup to Loan Setup on Hold',
        value: false,
        span: 'full',
        tooltip:
          'Sends one Outstanding Conditions email of missing or rejected Loan Intake items. Off until enabled.',
      },
    }),
  },
  {
    key: 'conditionTrigger',
    title: 'Condition Trigger',
    fields: () => ({
      triggerRule: {
        inputType: 'multiselect',
        title: 'Loan Status',
        options: loanGlobalStatus,
        defaultSelected: false,
      },
    }),
  },
  {
    key: 'contractorBuilder',
    title: 'Contractor/Builder',
    fields: () => ({
      constructionTrackValidation: {
        title: 'Min Construction Track Records',
        inputType: 'text',
        type: 'number',
      },
    }),
  },
  {
    key: 'closingDays',
    title: 'Closing Days',
    fields: () => ({
      closingDays: {
        title: 'Closing Days',
        inputType: 'text',
        type: 'number',
      },
    }),
  },
  {
    key: 'documentStorage',
    title: 'Document Storage',
    fields: () => ({
      fileExtensions: {
        title: 'Allowed Upload File Extension',
        inputType: 'multiselect',
        options: FileExtensionOptions,
        defaultSelected: false,
      },
    }),
  },
  {
    key: 'rushRestrictions',
    title: 'Rush Restrictions',
    fields: () => ({
      rushRestriction: {
        inputType: 'multiselect',
        title: 'Loan Status',
        options: loanGlobalStatus,
        defaultSelected: false,
      },
    }),
  },
  {
    key: 'manageAccounts',
    title: 'Manage Accounts',
    fields: () => ({
      activeLoanStatusForAEDisable: {
        inputType: 'multiselect',
        title: 'Once AE Disabled, Switch AE on loan when status',
        options: loanGlobalStatus,
      },
    }),
  },
  {
    key: 'borrowerReminder',
    title: 'Borrower Reminder',
    fields: () => ({
      isBorrowerReminderOn: {
        inputType: 'togglebutton',
        title: 'Email reminder on Expired Docs',
        span: 'full',
      },
      bReminderStartAfter: {
        inputType: 'text',
        type: 'thousandSepNoDecimal',
        title: 'Start ____ day(s) after expiration',
        tooltip: 'To start before expiration, input negative days',
      },
      bReminderStopAfter: {
        inputType: 'text',
        type: 'thousandSepNoDecimal',
        title: 'Stop ____ day(s) after expiration',
      },
    }),
  },
  {
    key: 'loanWithdrawnReminder1',
    title: 'Withdrawn Reminder (Inactivity)',
    fields: () => ({
      isLoanWithdrawnReminderOn1: {
        inputType: 'togglebutton',
        title: 'Loan Withdrawn Reminder on below status in period',
        span: 'full',
      },
      loanWithdrawnReminderStatus1: {
        inputType: 'multiselect',
        options: loanGlobalStatus,
        title: 'Loan status is in ...',
      },
      loanWithdrawnReminderPeriod1: {
        inputType: 'text',
        type: 'number',
        title: 'in ____ day(s)',
      },
      loanWithdrawnReminderAlarm1: {
        inputType: 'text',
        type: 'number',
        title: 'Send notification in ____ day(s)',
      },
      loanWithdrawnReminderAlarmAssignTo1: {
        inputType: 'multiselect',
        title: 'Notification will be sent to ...',
        options: TaskAssigneeOptions(),
      },
      loanWithdrawnReminderStatusTo1: {
        inputType: 'select',
        options: loanGlobalStatus,
        title: 'Loan Status will change to ...',
      },
      loanWithdrawnReminderWithdrawIfNoStatusChange1: {
        inputType: 'togglebutton',
        title: 'Withdraw if no Status Change',
      },
      loanWithdrawnReminderWithdrawIfNoDocumentUploaded1: {
        inputType: 'togglebutton',
        title: 'Withdrawn if No Document Uploaded',
      },
      loanWithdrawnReminderDocumentPeriod1: {
        inputType: 'text',
        type: 'number',
        title: 'in ____ day(s) (for document upload)',
      },
      loanWithdrawnReminderWithdrawIfNoStatusChangeButDocumentUploaded1: {
        inputType: 'togglebutton',
        title: 'Withdraw if No Status Change, but Document is uploaded',
        span: 'full',
      },
    }),
  },
  {
    key: 'loanWithdrawnReminder2',
    title: 'Withdrawn Reminder (Not)',
    fields: () => ({
      isLoanWithdrawnReminderOn2: {
        inputType: 'togglebutton',
        title: 'Loan Withdrawn Reminder on NOT below status in period',
        span: 'full',
      },
      loanWithdrawnReminderStatus2: {
        inputType: 'multiselect',
        options: loanGlobalStatus,
        title: 'Loan status is NOT in ...',
      },
      loanWithdrawnReminderPeriod2: {
        inputType: 'text',
        type: 'number',
        title: 'in ____ day(s)',
      },
      loanWithdrawnReminderAlarm2: {
        inputType: 'text',
        type: 'number',
        title: 'Send notification in ____ day(s)',
      },
      loanWithdrawnReminderAlarmAssignTo2: {
        inputType: 'multiselect',
        title: 'Notification will be sent to ...',
        options: TaskAssigneeOptions(),
      },
      loanWithdrawnReminderStatusTo2: {
        inputType: 'select',
        options: loanGlobalStatus,
        title: 'Loan Status will change to ...',
      },
      loanWithdrawnReminderWithdrawIfNoStatusChange2: {
        inputType: 'togglebutton',
        title: 'Withdraw if no Status Change',
      },
      loanWithdrawnReminderWithdrawIfNoDocumentUploaded2: {
        inputType: 'togglebutton',
        title: 'Withdrawn if No Document Uploaded',
      },
      loanWithdrawnReminderDocumentPeriod2: {
        inputType: 'text',
        type: 'number',
        title: 'in ____ day(s) (for document upload)',
      },
      loanWithdrawnReminderWithdrawIfNoStatusChangeButDocumentUploaded2: {
        inputType: 'togglebutton',
        title: 'Withdraw if No Status Change, but Document is uploaded',
        span: 'full',
      },
    }),
  },
  {
    key: 'lockExpiringReminder',
    title: 'Lock Expiring Reminder',
    fields: () => ({
      isLockExpiringReminderOn: {
        inputType: 'togglebutton',
        title: 'Send notification when rate lock is about to expire',
        span: 'full',
      },
      lockExpiringReminderDays: {
        inputType: 'text',
        type: 'number',
        title: 'Send notification ____ day(s) before lock expires',
      },
      lockExpiringReminderAssignTo: {
        inputType: 'multiselect',
        title: 'Notification will be sent to ...',
        options: TaskAssigneeOptions(),
      },
      lockExpiringReminderStatus: {
        inputType: 'multiselect',
        options: loanGlobalStatus,
        title: 'Only when loan status is in ...',
        span: 'full',
        tooltip: 'Leave blank to include all statuses except Funded, Withdrawn, and Declined.',
      },
    }),
  },
  {
    key: 'loanSubmission',
    title: 'Loan Submission',
    fields: () => ({
      submitToBytepro: {
        inputType: 'multiselect',
        title: 'Submit to Bytepro Button Visibility',
        options: loanGlobalStatus,
        defaultSelected: false,
      },
      initialPushToBytepro: {
        inputType: 'multiselect',
        title: 'Initial Push to Bytepro',
        options: { ...loanGlobalStatus, ...loanServicingStatus },
        defaultSelected: false,
        hasDefaultOption: true,
      },
      updatedPushToBytepro: {
        inputType: 'multiselect',
        title: 'Updated Push to Bytepro',
        options: { ...loanGlobalStatus, ...loanServicingStatus },
        defaultSelected: false,
      },
    }),
  },
  {
    key: 'paymentMethods',
    title: 'Payment Methods',
    fields: () => {
      const providerOptions = paymentProviderOptions(vendor)
      return {
        paymentProvider: {
          inputType: 'select',
          title: 'Default Payment Provider',
          options: providerOptions,
          // Keep the blank "- Select -" option: with a single enabled provider the stored value
          // (default 'stripe') often isn't in the options list, so without a blank option the
          // select shows the lone provider as if selected while the underlying value never
          // changes — no onChange fires, nothing saves. The blank gives an explicit selection to
          // make, which triggers the save.
          hasDefaultOption: true,
          defaultOptionText: '- Select Provider -',
          required: true,
          disabled: Object.keys(providerOptions).length === 0,
        },
        // Credit card / ACH / pinless per area, and the provider behind each, live in the
        // PaymentMethodsByArea grid below these fields.
        onlinePaymentAutoApply: {
          inputType: 'toggle',
          title: 'Auto-apply matching online servicing payments to the ledger',
          value: false,
          span: 'full',
        },
      }
    },
  },
  {
    key: 'servicing',
    title: 'Servicing',
    fields: () => ({
      payoffInterestAccrualEnd: {
        inputType: 'select',
        title: 'Payoff Interest Accrues',
        options: interestAccrualEndOptions,
        // No blank option: this is the company-wide fallback, so it always needs a concrete
        // value. An individual loan overrides it from Servicing Information.
        hasDefaultOption: false,
        span: 'full',
      },
      rateChangeAccrual: {
        inputType: 'select',
        title: 'Rate Change Mid-Period',
        options: rateChangeAccrualOptions,
        // Company-wide fallback; a loan overrides it from Servicing Information.
        hasDefaultOption: false,
        span: 'full',
        tooltip:
          'How a billing period is priced when a scheduled rate change (ARM, step rate, or one entered on Rate & Payment Changes) falls inside it. Applies to Dutch and Non-Dutch loans.',
      },
      rateChangeRecast: {
        inputType: 'select',
        title: 'Rate Change Payment',
        options: rateChangeRecastOptions,
        hasDefaultOption: false,
        span: 'full',
        tooltip:
          'On an amortizing loan, whether a rate change recalculates the P&I over the remaining term, from the first payment due after the change, or keeps the payment as it is.',
      },
      rateChangeNoticeEmail: {
        inputType: 'toggle',
        title: 'Email borrowers their Rate Change Notice for an ARM adjustment',
        value: true,
        span: 'full',
        tooltip:
          "The notice is always filed to the loan's documents when the adjustment is scheduled. On, it is emailed to the borrower the number of days below before the first payment at the new rate; off, a servicer reviews and emails it from Rate & Payment Changes.",
      },
      armNoticeLeadDays: {
        inputType: 'text',
        type: 'number',
        title: 'Email the ARM notice ____ days before the first payment at the new rate',
        value: 60,
        tooltip:
          'An adjustment scheduled further ahead than this waits, and the nightly run emails it once the payment is this close. 0 emails it as soon as the adjustment is scheduled. A notice for a payment already past is filed but never emailed.',
      },
      paymentOverpaymentAppliesTo: {
        inputType: 'select',
        title: 'Funds Paid Over the Amount Owed',
        options: overpaymentAppliesToOptions,
        // Company-wide rule with no per-loan override, so it always needs a concrete value.
        hasDefaultOption: false,
        span: 'full',
      },
      servicingPayoutChecks: {
        inputType: 'toggle',
        title: 'Queue servicing payouts (investor distributions and servicing fees) to the Check Queue',
        value: true,
        span: 'full',
      },
      borrowerPortalServicingSource: {
        inputType: 'select',
        title: 'Borrower Portal Shows',
        options: borrowerPortalServicingSourceOptions,
        // Company-wide fallback, so it always needs a concrete value. An individual loan
        // overrides it from Servicing Information.
        hasDefaultOption: false,
        span: 'full',
      },
    }),
  },
  {
    // Recurring ACH the borrower manages themselves from the portal. The drafts still go out in the
    // servicer's own ACH runs from the same ACH record the loan's ACH tab edits.
    key: 'borrowerAutopay',
    title: 'Borrower Portal AutoPay (ACH)',
    fields: () => ({
      borrowerAchAutopayEnabled: {
        inputType: 'toggle',
        title: 'Show AutoPay in the borrower portal (off hides it entirely)',
        value: false,
        span: 'full',
      },
      borrowerAchAutopaySelfEnroll: {
        inputType: 'toggle',
        title: 'Let borrowers turn AutoPay on and off themselves',
        value: true,
        span: 'full',
      },
      borrowerAchBankUpdate: {
        inputType: 'toggle',
        title: 'Let borrowers enter and update the bank account AutoPay drafts from',
        value: true,
        span: 'full',
      },
      borrowerAchAuthorizationText: {
        inputType: 'textarea',
        title: 'AutoPay Authorization (leave blank for the standard wording)',
        value: '',
        span: 'full',
        tooltip:
          'Shown above the checkbox a borrower ticks to enroll or change accounts, and stored word for word ' +
          'with each authorization they give.',
      },
    }),
  },
  {
    // Texting is not a servicing feature: one switch and one number serve every loan the company
    // has, origination included, because the settings are read per loan and never per pipeline.
    // It sat under Servicing only because that is where the screen was built first, which read as
    // if origination were excluded.
    key: 'textMessaging',
    title: 'Text Messaging',
    fields: () => ({
      borrowerTextingEnabled: {
        inputType: 'toggle',
        title: 'Enable two-way text messaging with borrowers (all loans, origination and servicing)',
        value: false,
        span: 'full',
      },
      borrowerTextingFromNumber: {
        inputType: 'text',
        type: 'phone',
        title: 'Text Messaging Number (the number borrowers see and reply to)',
        value: '',
        span: 'full',
      },
      borrowerTextingRequireConsent: {
        inputType: 'toggle',
        // Off by default so turning consent tracking on cannot silently stop a servicer who is
        // already texting. Turn it ON wherever the A2P campaign tells carriers that borrowers
        // opt in through the portal, because that claim then has to be true of the running system.
        title: 'Only text borrowers who have opted in (Edit Profile > Text Messages, or texting JOIN)',
        value: false,
        span: 'full',
      },
    }),
  },
  {
    key: 'payoffWire',
    title: 'Payoff Wire Instructions',
    fields: () => ({
      payoffWirePayableTo: {
        inputType: 'text',
        title: 'Funds Payable To (blank uses the company name)',
        value: '',
        span: 'full',
      },
      payoffWireBankName: {
        inputType: 'text',
        title: 'Bank',
        value: '',
      },
      payoffWireBankAddress: {
        inputType: 'text',
        title: 'Bank Address',
        value: '',
      },
      payoffWireBeneficiary: {
        inputType: 'text',
        title: 'Beneficiary',
        value: '',
      },
      payoffWireBeneficiaryAddress: {
        inputType: 'text',
        title: 'Beneficiary Address',
        value: '',
      },
      payoffWireRoutingNumber: {
        type: 'routingNo',
        inputType: 'text',
        title: 'ABA Routing #',
        value: '',
      },
      payoffWireAccountNumber: {
        type: 'bankAccountNo',
        inputType: 'text',
        title: 'Account #',
        value: '',
      },
      payoffWireSwiftCode: {
        inputType: 'text',
        title: 'SWIFT Code',
        value: '',
      },
      payoffWireFurtherCredit: {
        inputType: 'text',
        title: 'For Further Credit To',
        value: '',
      },
      payoffWireMaskAccountNumber: {
        inputType: 'toggle',
        title: 'Mask the last four digits of the account number on the statement',
        value: true,
        span: 'full',
      },
      payoffWireVerifyPhone: {
        inputType: 'text',
        type: 'phone',
        title: 'Number to Call to Confirm the Wire (blank uses the company phone)',
        value: '',
      },
      payoffWireCutoffTime: {
        inputType: 'text',
        title: 'Wire Cutoff Time (blank drops the sentence)',
        value: '',
      },
      payoffWireAdditionalInstructions: {
        inputType: 'textarea',
        title: 'Additional Wire Instructions',
        value: '',
        span: 'full',
      },
      payoffAcceptedFundsMethods: {
        inputType: 'text',
        title: 'Funds Are Accepted By (prints as "will be accepted by ...")',
        value: 'WIRE ONLY',
        span: 'full',
      },
      payoffEscrowStatement: {
        inputType: 'textarea',
        title: 'Escrow Disposition Sentence on the Payoff (blank drops the sentence)',
        value: '',
        span: 'full',
      },
    }),
  },
  {
    key: 'tasks',
    title: 'Tasks',
    fields: () => ({
      clearAllTasks: {
        inputType: 'multiselect',
        title: 'Clear All Tasks',
        options: { ...loanGlobalStatus, ...loanServicingStatus },
        defaultSelected: false,
      },
      clearAllReminders: {
        inputType: 'multiselect',
        title: 'Clear All Reminders',
        options: { ...loanGlobalStatus, ...loanServicingStatus },
        defaultSelected: false,
      },
      internalAccountTypes: {
        inputType: 'multiselect',
        title: 'Internal Account Types',
        options: accountTypes,
        defaultSelected: false,
      },
    }),
  },
  {
    key: 'counterpartyApproval',
    title: 'Counterparty Approval',
    fields: () => ({
      counterpartyApprovalAutoNotifyFrequency: {
        inputType: 'text',
        type: 'number',
        title: 'Days Reminder Frequency',
      },
      counterpartyApprovalAutoNotifyTime: {
        inputType: 'select',
        title: 'Time',
        options: timesOption,
        hasDefaultOption: false,
      },
      counterpartyApprovalAutoNotifyMax: {
        inputType: 'text',
        type: 'number',
        title: 'Maximum Notifications',
      },
    }),
  },
  {
    key: 'restructureLoanRelease',
    title: 'Restructure Loan Release',
    fields: () => ({}),
  },
]

export const notificationConfigKeys = [
  'borrowerAutoNotifyLoanStatus',
  'borrowerAutoNotifyFrequency',
  'borrowerAutoNotifyTime',
  'borrowerAutoNotifyLoanTypes',
  'defaultNotifyParties',
  'autoNotifyOnHoldTransition',
]
